/**
 * Iter 67bc — Universal invoice delivery actions.
 *
 * One self-contained block that handles "Download / Email / WhatsApp" for both
 *   • Stream A · rental invoices (vendor → customer)        kind="rental"
 *   • Stream B · platform invoices (admin → vendor)         kind="platform"
 *
 * Mounted on every dashboard role's invoice view so every user has parity
 * (customer can re-download/forward, vendor can resend, admin can chase).
 *
 * Layout modes:
 *   variant="full"   — three labelled buttons (used on detail pages)
 *   variant="icons"  — three icon-only ghost buttons (used in list rows)
 */
import { useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Download, Mail, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

// `kind`: "rental" | "platform" — drives the endpoint base path. Keeping this
// here (rather than passing full URLs from the caller) means callers only
// need to know which invoice family they're working with.
const PATHS = {
  rental: {
    pdf:      (id) => `/invoices/${id}/pdf`,
    email:    (id) => `/invoices/${id}/send-email`,
    whatsapp: (id) => `/invoices/${id}/send-whatsapp`,
  },
  platform: {
    pdf:      (id) => `/vendor/platform-invoices/${id}/pdf`,
    email:    (id) => `/vendor/platform-invoices/${id}/send-email`,
    whatsapp: (id) => `/vendor/platform-invoices/${id}/send-whatsapp`,
  },
};

function _absPdfUrl(path) {
  // Axios base path + path; we need the absolute URL the browser can open
  // in a new tab (cookies travel because we're same-origin).
  return `${API}${path}`;
}

export default function InvoiceDeliveryActions({
  kind = "rental",
  invoiceId,
  invoiceNumber,
  defaultEmail = "",
  defaultWhatsapp = "",
  variant = "full",
  className = "",
}) {
  const paths = PATHS[kind] || PATHS.rental;
  const [emailOpen, setEmailOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailTo, setEmailTo] = useState(defaultEmail || "");
  const [emailMsg, setEmailMsg] = useState("");
  const [waTo, setWaTo] = useState(defaultWhatsapp || "");
  const [waMsg, setWaMsg] = useState("");

  const onDownload = () => {
    // The PDF endpoint returns `Content-Disposition: inline` so a plain
    // window.open shows it in the browser's native PDF viewer (and the
    // user gets a download button in the toolbar). Auth travels via the
    // same-origin httpOnly cookie — no token plumbing needed.
    window.open(_absPdfUrl(paths.pdf(invoiceId)), "_blank", "noopener,noreferrer");
  };

  const _doSend = async (endpoint, body, successMsg) => {
    setBusy(true);
    try {
      await api.post(endpoint, body);
      toast.success(successMsg);
      return true;
    } catch (err) {
      toast.error(formatApiError(err) || "Send failed.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSendEmail = async () => {
    if (!emailTo.trim()) { toast.error("Enter a recipient email."); return; }
    const ok = await _doSend(paths.email(invoiceId), {
      to: emailTo.trim(),
      message: emailMsg.trim() || null,
    }, `Invoice emailed to ${emailTo.trim()}`);
    if (ok) { setEmailOpen(false); setEmailMsg(""); }
  };

  const onSendWhatsapp = async () => {
    if (!waTo.trim()) { toast.error("Enter a recipient phone number."); return; }
    const ok = await _doSend(paths.whatsapp(invoiceId), {
      to: waTo.trim(),
      message: waMsg.trim() || null,
    }, `Invoice WhatsApp'd to ${waTo.trim()}`);
    if (ok) { setWaOpen(false); setWaMsg(""); }
  };

  const isIcons = variant === "icons";
  const baseClass = isIcons ? "h-8 w-8 p-0" : "";
  const idSuffix = invoiceId ? invoiceId.slice(0, 8) : "x";

  return (
    <div className={`flex items-center gap-1.5 ${className}`} data-testid={`invoice-delivery-actions-${idSuffix}`}>
      <Button
        type="button"
        variant={isIcons ? "ghost" : "outline"}
        size={isIcons ? "icon" : "sm"}
        className={`${baseClass} ${!isIcons ? "rounded-full" : ""}`}
        onClick={onDownload}
        title="Download PDF"
        data-testid={`invoice-download-${idSuffix}`}
      >
        <Download className={isIcons ? "h-3.5 w-3.5" : "h-3.5 w-3.5 mr-1.5"} />
        {!isIcons && "Download"}
      </Button>
      <Button
        type="button"
        variant={isIcons ? "ghost" : "outline"}
        size={isIcons ? "icon" : "sm"}
        className={`${baseClass} ${!isIcons ? "rounded-full" : ""}`}
        onClick={() => setEmailOpen(true)}
        title="Send via email"
        data-testid={`invoice-email-${idSuffix}`}
      >
        <Mail className={isIcons ? "h-3.5 w-3.5" : "h-3.5 w-3.5 mr-1.5"} />
        {!isIcons && "Email"}
      </Button>
      <Button
        type="button"
        variant={isIcons ? "ghost" : "outline"}
        size={isIcons ? "icon" : "sm"}
        className={`${baseClass} ${!isIcons ? "rounded-full" : ""} text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50`}
        onClick={() => setWaOpen(true)}
        title="Send via WhatsApp"
        data-testid={`invoice-whatsapp-${idSuffix}`}
      >
        <MessageCircle className={isIcons ? "h-3.5 w-3.5" : "h-3.5 w-3.5 mr-1.5"} />
        {!isIcons && "WhatsApp"}
      </Button>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={(o) => !busy && setEmailOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid={`invoice-email-dialog-${idSuffix}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" /> Email invoice {invoiceNumber || ""}
            </DialogTitle>
            <DialogDescription>The PDF will be attached to the email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor={`email-to-${idSuffix}`}>Recipient email</Label>
              <Input
                id={`email-to-${idSuffix}`}
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="customer@example.com"
                className="mt-1"
                data-testid={`invoice-email-to-${idSuffix}`}
              />
            </div>
            <div>
              <Label htmlFor={`email-msg-${idSuffix}`}>Personal note (optional)</Label>
              <Textarea
                id={`email-msg-${idSuffix}`}
                value={emailMsg}
                onChange={(e) => setEmailMsg(e.target.value)}
                placeholder="e.g. Thanks for renting with us!"
                rows={3}
                className="mt-1"
                data-testid={`invoice-email-msg-${idSuffix}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={onSendEmail}
              disabled={busy || !emailTo.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid={`invoice-email-submit-${idSuffix}`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Mail className="h-4 w-4 mr-1.5" />}
              Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp dialog */}
      <Dialog open={waOpen} onOpenChange={(o) => !busy && setWaOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid={`invoice-whatsapp-dialog-${idSuffix}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-600" /> WhatsApp invoice {invoiceNumber || ""}
            </DialogTitle>
            <DialogDescription>The PDF will be sent as a WhatsApp attachment (Twilio media).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor={`wa-to-${idSuffix}`}>Recipient WhatsApp number</Label>
              <Input
                id={`wa-to-${idSuffix}`}
                type="tel"
                value={waTo}
                onChange={(e) => setWaTo(e.target.value)}
                placeholder="+40712345678"
                className="mt-1"
                data-testid={`invoice-whatsapp-to-${idSuffix}`}
              />
              <p className="text-xs text-neutral-500 mt-1">International format (+ country code).</p>
            </div>
            <div>
              <Label htmlFor={`wa-msg-${idSuffix}`}>Personal note (optional)</Label>
              <Textarea
                id={`wa-msg-${idSuffix}`}
                value={waMsg}
                onChange={(e) => setWaMsg(e.target.value)}
                placeholder="e.g. Here's your invoice — drive safe!"
                rows={3}
                className="mt-1"
                data-testid={`invoice-whatsapp-msg-${idSuffix}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWaOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={onSendWhatsapp}
              disabled={busy || !waTo.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid={`invoice-whatsapp-submit-${idSuffix}`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <MessageCircle className="h-4 w-4 mr-1.5" />}
              Send WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
